import type {
  CompetitionId,
  CompetitionView,
  EventBasedMarketView,
  EventId,
  EventTypeView,
  EventView,
  MarketId
} from '@ps/isp-web-services-types';
import type { Selector } from 'react-redux';

import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import type {
  RootState,
  SelectorConfig,
  SelectorCreator,
  Slicer,
  WidgetModelConfig
} from '@swf/core/store';
import type { SportsWidgetConfig } from '@swf/sports/pages/types';
import type { EventTypeId } from '@swf/sports/types/eventType';

import type { EventBasedMarketTemplatesView } from '../../types/templates';

export type CompetitionsSelector = Selector<
  RootState,
  EventBasedMarketTemplatesView['competitions']
>;
export type EventsSelector = Selector<RootState, EventBasedMarketTemplatesView['events']>;
export type EventTypesSelector = Selector<RootState, EventBasedMarketTemplatesView['eventTypes']>;
export type MarketsSelector = Selector<RootState, EventBasedMarketTemplatesView['markets']>;

const initialState: EventBasedMarketTemplatesView = {
  competitions: {},
  events: {},
  eventTypes: {},
  markets: {}
};

export const selectors = (
  slice: Slicer<EventBasedMarketTemplatesView>,
  createSelector: SelectorCreator
) => ({
  competitions: (): CompetitionsSelector => slice((state) => state.competitions),
  competition(this: { competitions: CompetitionsSelector }) {
    return createSelector(
      this.competitions,
      (_state: RootState, competitionId: CompetitionId) => competitionId,
      (competitions, competitionId): CompetitionView => competitions[competitionId]
    );
  },
  events: (): EventsSelector => slice((state) => state.events),
  event(this: { events: EventsSelector }) {
    return createSelector(
      this.events,
      (_state: RootState, eventId: EventId) => eventId,
      (events, eventId): EventView => events[eventId]
    );
  },
  eventTypes: (): EventTypesSelector => slice((state) => state.eventTypes),
  eventType(this: { eventTypes: EventTypesSelector }) {
    return createSelector(
      this.eventTypes,
      (_state: RootState, eventTypeId: EventTypeId) => eventTypeId,
      (eventTypes, eventTypeId): EventTypeView => eventTypes[eventTypeId] as EventTypeView
    );
  },
  markets(this: { markets: MarketsSelector }) {
    return createSelector(
      slice,
      (_state: RootState, marketIds?: MarketId[]) => marketIds,
      ({ markets }, marketIds) => {
        if (!marketIds) {
          return markets;
        }

        return marketIds.reduce(
          (acc, marketId) => {
            if (markets[marketId]) {
              return {
                ...acc,
                [marketId]: markets[marketId]
              };
            }

            return acc;
          },
          {} as typeof markets
        );
      }
    );
  },
  market(this: { markets: MarketsSelector }) {
    return createSelector(
      (state: RootState) => this.markets(state),
      (_state: RootState, marketId: MarketId) => marketId,
      (markets, marketId): EventBasedMarketView | undefined => markets[marketId]
    );
  },
  marketIds(this: { markets: MarketsSelector }) {
    return createSelector(this.markets, (markets) => Object.keys(markets));
  },
  marketTemplateData(this: {
    competitions: CompetitionsSelector;
    events: EventsSelector;
    eventTypes: EventTypesSelector;
    markets: MarketsSelector;
  }) {
    return createSelector(
      this.competitions,
      this.events,
      this.eventTypes,
      this.markets,
      (competitions, events, eventTypes, markets): EventBasedMarketTemplatesView => ({
        competitions,
        events,
        eventTypes,
        markets
      })
    );
  },
  participants(this: { events: EventsSelector }) {
    return createSelector(
      this.events,
      (_state: RootState, eventId: EventId) => eventId,
      (events, eventId) => events[eventId].enrichedParticipants
    );
  },
  /**
   * @deprecated GSFE-2137 - use "marketTemplateData" instead
   */
  getMarketTemplateData(this: {
    competitions: CompetitionsSelector;
    events: EventsSelector;
    eventTypes: EventTypesSelector;
    markets: MarketsSelector;
  }) {
    return createSelector(
      this.competitions,
      this.events,
      this.eventTypes,
      this.markets,
      (competitions, events, eventTypes, markets): EventBasedMarketTemplatesView => ({
        competitions,
        events,
        eventTypes,
        markets
      })
    );
  }
});

export const withEventBasedMarketTemplateModelState = <T>(
  modelState: T
): T & EventBasedMarketTemplatesView => ({ ...initialState, ...modelState });

export function withEventBasedMarketTemplateModelSelectors<
  S extends EventBasedMarketTemplatesView,
  F extends (slice: Slicer<S>, createSelector: SelectorCreator) => SelectorConfig<S>
>(_state: S, modelSelectorFactory: F) {
  return (slice: Slicer<S>, createSelector: SelectorCreator) =>
    ({
      ...selectors(slice, createSelector),
      ...modelSelectorFactory(slice, createSelector)
    }) as ReturnType<typeof selectors> & ReturnType<F>;
}

export interface EventMarketTemplatesModel
  extends WidgetModelConfig<SportsWidgetConfig, EventBasedMarketTemplatesView> {
  selectors: typeof selectors;
}

// @ts-ignore fix type mismatch
export const useEventMarketTemplatesWidget: UseWidget<void, EventMarketTemplatesModel> = useWidget;
